// src/components/Pages/FICSPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react'; // Added useCallback, useRef
import {
  Box,
  Button,
  Container,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  Title,
  Loader,
  Alert,
  useMantineTheme,
  useMantineColorScheme,
  RingProgress,
  SimpleGrid,
  Badge,
  Anchor
} from '@mantine/core';
import { IconAlertCircle, IconBulb, IconFileText } from '@tabler/icons-react';
import { useSearchParams } from 'react-router-dom'; // Added useLocation, removed useNavigate

import PublishedOverlaps from '../FICS/PublishedOverlaps';
import DraftConflict from '../FICS/DraftConflict';
import CompetitorAnalysis from '../FICS/CompetitorAnalysis';

import { useBackgroundImage } from '../../context/BackgroundImageContext';
import backgroundImageFile from '../../assets/images/brochure-background.jpg';
import { useAuth } from '../../context/AuthContext';

const verticals = ["LoveMoney", "LoveFood", "LoveProperty", "LoveExploring", "General"]; // Make sure this is comprehensive or passed if needed

// --- Helper functions (Unchanged from your script) ---
const calculateTopSimilarity = (items) => {
  if (!items || items.length === 0) return null;
  let minDistance = Infinity;
  let foundValidItem = false;
  items.forEach(item => {
    if (typeof item.distance === 'number' && item.distance >= 0 && item.distance <= 1) {
      if (item.distance < minDistance) {
        minDistance = item.distance;
        foundValidItem = true;
      }
    }
  });
  if (!foundValidItem) return null;
  return Math.round((1 - minDistance) * 100);
};

const getDraftSimilarity = (draftDetails) => {
  if (draftDetails && draftDetails.closest_conflicting_draft && typeof draftDetails.closest_conflicting_draft.distance === 'number') {
    const distance = draftDetails.closest_conflicting_draft.distance;
    if (distance >= 0 && distance <= 1) {
      return Math.round((1 - distance) * 100);
    }
  }
  return null;
};

const deriveQuartileScoreLabel = (score) => {
  if (score === null || score === undefined || isNaN(parseFloat(score))) return "N/A";
  const numericScore = parseFloat(score);
  if (numericScore >= 7.5) return 'Very High';
  if (numericScore >= 5.0) return 'High';
  if (numericScore >= 2.5) return 'Moderate';
  if (numericScore >= 0) return 'Low';
  return "N/A";
};

const getSimilarityRingColor = (percentage) => {
  if (percentage === null || percentage === undefined) return 'gray';
  if (percentage > 70) return 'red';
  if (percentage > 50) return 'orange';
  if (percentage > 30) return 'yellow';
  return 'blue.4';
};
// --- End Helper functions ---

function FICSPage() {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const { isBackgroundImageVisible } = useBackgroundImage();
  const { currentUser } = useAuth();

  const [searchParams, setSearchParams] = useSearchParams(); // <<< Use useSearchParams
  //const navigate = useNavigate();

  // Initialize states, now from searchParams
  const initialTitleFromQuery = searchParams.get('initialTitle');
  const initialVerticalFromQuery = searchParams.get('initialVertical'); // If you decide to pass it


  // Initialize states, potentially from location.state passed by redirect
  const [ideaTitle, setIdeaTitle] = useState(initialTitleFromQuery || '');
  const [selectedVertical, setSelectedVertical] = useState(
    initialVerticalFromQuery && verticals.includes(initialVerticalFromQuery)
      ? initialVerticalFromQuery
      : verticals[0]
  );

  const [isLoading, setIsLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [summaryStats, setSummaryStats] = useState(null);

  // Ref to track the title that was received via redirect and intended for auto-submission.
  // This helps ensure auto-submit happens only once for that specific title.
  const autoSubmitTitleRef = useRef(null);

  // Core FICS analysis logic, wrapped in useCallback for stable reference
  const performFicsAnalysis = useCallback(async () => {
    if (!ideaTitle.trim()) {
      setApiError("Please enter an idea title.");
      // Do not set isLoading to false here if it wasn't set to true
      return;
    }
    if (!currentUser) {
      setApiError("User not authenticated. Please log in again.");
      setIsLoading(false); // Only set isLoading false if an actual attempt was stopped
      return;
    }

    console.log(`FICSPage: Starting analysis for title: "${ideaTitle}", vertical: "${selectedVertical}"`);
    setIsLoading(true);
    setApiResponse(null); // Clear previous results
    setApiError(null);   // Clear previous errors
    setSummaryStats(null); // Clear previous summary stats

    let idToken;
    try {
      idToken = await currentUser.getIdToken();
    } catch (tokenError) {
      console.error("FICSPage: Error getting ID token:", tokenError);
      setApiError("Could not verify user session. Please log in again.");
      setIsLoading(false);
      return;
    }

    const payload = { idea_text: ideaTitle, vertical: selectedVertical };
    const ficsApiUrl = 'https://process-freelancer-idea-u6n4kc7zyq-ew.a.run.app'; // Your FICS API URL

    try {
      const response = await fetch(ficsApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "An unknown error occurred processing the API response." }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setApiResponse(data);
      console.log("FICSPage: Analysis successful.", data);
    } catch (error) {
      console.error("FICSPage: API call error:", error);
      setApiError(error.message || "Failed to fetch results from API.");
    } finally {
      setIsLoading(false);
    }
  }, [ideaTitle, selectedVertical, currentUser, /* Include other stable dependencies if any, like API URL if it were dynamic */]);

  // Effect 1: Process incoming title/vertical from query parameters
  useEffect(() => {
    const redirectedTitle = searchParams.get('initialTitle');
    const redirectedVertical = searchParams.get('initialVertical'); // If you are passing this

    // Check if redirectedTitle exists and if it's different from the one already processed
    // This helps prevent re-processing if this effect runs multiple times before params are cleared.
    if (redirectedTitle && redirectedTitle !== autoSubmitTitleRef.current) {
      console.log("FICSPage: Received data from query params - Title:", redirectedTitle, "Vertical:", redirectedVertical);
      setIdeaTitle(redirectedTitle);
      if (redirectedVertical && verticals.includes(redirectedVertical)) {
        setSelectedVertical(redirectedVertical);
      }
      // No need to set selectedVertical if not passed, it will use its default or current state

      autoSubmitTitleRef.current = redirectedTitle; // Mark this title as the one to auto-submit

      // Clear the specific query parameters from the URL after processing
      // This makes the URL cleaner and prevents re-processing on simple refresh.
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('initialTitle');
      if (redirectedVertical) { // Only delete if it was potentially there
        newSearchParams.delete('initialVertical');
      }
      setSearchParams(newSearchParams, { replace: true }); // Use replace to not add to history

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [searchParams, setSearchParams]);

  // Effect 2: Trigger auto-analysis if conditions are met
  useEffect(() => {
    // Check if:
    // 1. autoSubmitTitleRef.current has a title (meaning a redirect just occurred and set it)
    // 2. The current ideaTitle in state matches this (ensuring state update has happened)
    // 3. currentUser is available
    // 4. Not currently loading
    if (autoSubmitTitleRef.current &&
      ideaTitle === autoSubmitTitleRef.current &&
      currentUser &&
      !isLoading) {

      console.log("FICSPage: Conditions met for auto-submission for title:", ideaTitle);
      performFicsAnalysis();
      autoSubmitTitleRef.current = null; // Reset ref after initiating submission to prevent re-triggering for this same title
    }
  }, [ideaTitle, currentUser, isLoading, performFicsAnalysis]); // performFicsAnalysis is stable due to useCallback


  // Existing useEffect to calculate summaryStats from apiResponse (remains the same)
  useEffect(() => {
    if (apiResponse) {
      const newSummary = {
        topPublishedSimilarity: calculateTopSimilarity(apiResponse.published_overlaps),
        draftSimilarity: getDraftSimilarity(apiResponse.draft_conflict_details),
        closestCompetitorSimilarity: calculateTopSimilarity(apiResponse.competitor_analysis?.top_matches),
        prominencePotential: deriveQuartileScoreLabel(apiResponse.competitor_analysis?.average_prominence_score),
        stickinessExpected: deriveQuartileScoreLabel(apiResponse.competitor_analysis?.average_stickiness_score),
      };
      setSummaryStats(newSummary);
    } else {
      setSummaryStats(null); // Clear summary if no apiResponse
    }
  }, [apiResponse]);

  // Manual submit handler for the form button
  const handleIdeaCheck = (e) => {
    if (e) e.preventDefault(); // Prevent default form submission if called by an event
    autoSubmitTitleRef.current = null; // User is taking manual control, cancel any pending auto-submit intent
    performFicsAnalysis();
  };

  // Theme-aware styles (from your script)
  const sectionContainerBg = isDark ? theme.colors.dark[6] : theme.colors.gray[0];
  const lightInputStyle = {
    backgroundColor: theme.colors.gray[4], color: theme.black, borderColor: theme.colors.gray[4],
    '&::placeholder': { color: theme.colors.gray[6] },
    '&:focus, &:focus-within': { borderColor: theme.colors.blue[5] },
  };
  const darkInputStyle = {
    backgroundColor: theme.colors.dark[4], color: theme.white, borderColor: theme.colors.dark[4],
    '&::placeholder': { color: theme.colors.gray[5] },
    '&:focus, &:focus-within': { borderColor: theme.colors.blue[7] },
  };

  const inputBaseStyles = isDark ? darkInputStyle : lightInputStyle; // Corrected from your FICSPage
  const textareaStyles = { input: inputBaseStyles };
  const selectStyles = { input: inputBaseStyles };
  const ringProgressTrackColor = isDark ? theme.colors.dark[4] : theme.colors.gray[6]; // As per your FICSPage
  const textOnBackgroundImageColor = isBackgroundImageVisible ? theme.white : (isDark ? theme.white : theme.black); // Adjusted for better default


  let briefBuilderLink = ''; // Default to an empty string

  // Only construct the link if the API has responded with the data we need
  if (apiResponse) {
    const params = new URLSearchParams();

    // Pass the title being analysed
    params.set('initialTitle', ideaTitle);

    // Pass the selected vertical to pre-populate the 'Target Brand' dropdown
    params.set('targetBrand', selectedVertical);

    // Gather all competitor URLs from the analysis
    const competitorUrls = apiResponse.competitor_analysis?.top_matches
      ?.map(match => match.url)
      .filter(Boolean); // This gets all valid URLs and filters out any empty ones

    if (competitorUrls && competitorUrls.length > 0) {
      // Join URLs with a newline character, which will be encoded
      params.set('initialUrls', competitorUrls.join('\n'));
    }

    briefBuilderLink = `/brief-builder?${params.toString()}`;
  }

  return (
    <Box
      style={{
        width: '100%',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundImage: isBackgroundImageVisible ? `url(${backgroundImageFile})` : 'none',
        backgroundColor: !isBackgroundImageVisible ? (isDark ? theme.colors.dark[7] : theme.colors.gray[0]) : 'transparent', // Added background color when image is off
        backgroundSize: isBackgroundImageVisible ? 'cover' : undefined,
        backgroundPosition: isBackgroundImageVisible ? 'center' : undefined,
        backgroundRepeat: isBackgroundImageVisible ? 'no-repeat' : undefined,
        backgroundAttachment: isBackgroundImageVisible ? 'fixed' : undefined,
        overflow: 'auto',
        scrollBehavior: 'smooth',
      }}
    >
      <Container
        size="lg"
        py="xl"
        style={{
          width: '100%',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack gap="md">
          <Title order={1} ta="center" style={{ color: textOnBackgroundImageColor }}>
            <Group justify="center" gap="xs">
              <IconBulb size={30} stroke={1.5} color={theme.colors.yellow[isDark || isBackgroundImageVisible ? 4 : 6]} />
              Idea Checker
            </Group>
          </Title>
          <Text ta="center" style={{ color: textOnBackgroundImageColor }}>
            Enter your new gallery idea here to generate a Writer Hub™ similarity report.
          </Text>

          <Paper
            withBorder shadow="md" p="xl" radius="md" mt="lg" component="form"
            bg={sectionContainerBg}
            onSubmit={handleIdeaCheck} // Manual submission
          >
            <Stack gap="lg">
              <Title order={2} >| Your Idea</Title>
              <Textarea
                size="lg"
                label="Enter your article title here: "
                placeholder="E.g. You Probably Didn't Know These 31 Surprising Facts About America's National Parks..."
                value={ideaTitle}
                onChange={(event) => {
                  setIdeaTitle(event.currentTarget.value);
                  autoSubmitTitleRef.current = null; // User interaction cancels auto-submit intent
                }}
                autosize
                minRows={2}
                required
                styles={textareaStyles}
              />
              <Select
                size="lg"
                label="Target Brand/Topic:"
                data={verticals}
                value={selectedVertical}
                onChange={(value) => {
                  setSelectedVertical(value);
                  autoSubmitTitleRef.current = null; // User interaction cancels auto-submit intent
                }}
                allowDeselect={false}
                required
                styles={selectStyles}
              />
              <Button type="submit" loading={isLoading} fullWidth mt="md" size="lg" variant="gradient"
                gradient={{ from: 'indigo.9', to: 'blue.4', deg: 105 }}>
                {isLoading ? 'Checking Idea...' : 'Check Idea'}
              </Button>
            </Stack>
          </Paper>

          {apiError && (<Alert title="Error" color="red" icon={<IconAlertCircle />} mt="md" withCloseButton onClose={() => setApiError(null)}>{apiError}</Alert>)}
          {isLoading && (<Group justify="center" mt="lg"><Loader /><Text ml="sm" style={{ color: textOnBackgroundImageColor }} >Creating embeddings / vectorising / analysing... (NB: Cold starts can take a minute. Once warm, it's speedy!)</Text></Group>)}

          {/* Results Display Section (remains unchanged from your script) */}
          {apiResponse && (
            <Paper withBorder shadow="xs" p="lg" radius="md" mt="xl" bg={sectionContainerBg}>
              <Title order={2} mb="sm">| Semantic AI Results</Title>
              <Text ml="sm" mb="lg" c={isDark ? theme.colors.gray[5] : theme.colors.gray[7]}>
                An analysis of thematically similar content.
              </Text>
              {summaryStats && (
                <Paper withBorder p="md" radius="md" mb="xl" shadow="xs" bg={isDark ? theme.colors.dark[7] : theme.white}>
                  {/* ... Your SimpleGrid for summaryStats ... */}
                  <SimpleGrid cols={{ base: 1, xs: 2, sm: 2, md: 5 }} spacing="lg" verticalSpacing="lg">
                    <Anchor href="#published-history-section" underline="never">
                      <Stack align="center" gap={theme.spacing.xs}>
                        <Text c={isDark ? theme.colors.dark[0] : theme.colors.gray[8]} size="md" ta="center">Archive Similarity</Text>
                        {summaryStats.topPublishedSimilarity !== null ? (
                          <RingProgress size={120} thickness={10} roundCaps
                            label={<Text c={getSimilarityRingColor(summaryStats.topPublishedSimilarity)} fw={700} ta="center" size="xl">{summaryStats.topPublishedSimilarity}%</Text>}
                            sections={[
                              { value: summaryStats.topPublishedSimilarity, color: getSimilarityRingColor(summaryStats.topPublishedSimilarity) },
                              { value: Math.max(0, 100 - summaryStats.topPublishedSimilarity), color: ringProgressTrackColor }
                            ]} />
                        ) : <Text size="lg" fw={500} c="dimmed">N/A</Text>}
                      </Stack>
                    </Anchor>
                    {/* ... Other Anchors for Drafts, Competitor Sim, Prominence, Stickiness ... */}
                    <Anchor href="#draft-conflicts-section" underline="never">
                      <Stack align="center" gap={theme.spacing.xs}>
                        <Text c={isDark ? theme.colors.dark[0] : theme.colors.gray[8]} size="md" ta="center">Drafts Similarity</Text>
                        {summaryStats.draftSimilarity !== null ? (
                          <RingProgress size={120} thickness={10} roundCaps
                            label={<Text c={getSimilarityRingColor(summaryStats.draftSimilarity)} fw={700} ta="center" size="xl">{summaryStats.draftSimilarity}%</Text>}
                            sections={[
                              { value: summaryStats.draftSimilarity, color: getSimilarityRingColor(summaryStats.draftSimilarity) },
                              { value: Math.max(0, 100 - summaryStats.draftSimilarity), color: ringProgressTrackColor }
                            ]} />
                        ) : (apiResponse?.draft_conflict_details?.status === 'none' ?
                          <Box style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                            <Text size="xl" c={theme.colors.green[isDark ? 4 : 7]} fw={500}>No Conflicts.</Text>
                          </Box>
                          : <Text size="lg" fw={500} c="dimmed">N/A</Text>
                        )}
                      </Stack>
                    </Anchor>
                    <Anchor href="#competitor-analysis-section" underline="never">
                      <Stack align="center" gap={theme.spacing.xs}>
                        <Text c={isDark ? theme.colors.dark[0] : theme.colors.gray[8]} size="md" ta="center">Competitor Similarity</Text>
                        {summaryStats.closestCompetitorSimilarity !== null ? (
                          <RingProgress size={120} thickness={10} roundCaps
                            label={<Text c={getSimilarityRingColor(summaryStats.closestCompetitorSimilarity)} fw={700} ta="center" size="xl">{summaryStats.closestCompetitorSimilarity}%</Text>}
                            sections={[
                              { value: summaryStats.closestCompetitorSimilarity, color: getSimilarityRingColor(summaryStats.closestCompetitorSimilarity) },
                              { value: Math.max(0, 100 - summaryStats.closestCompetitorSimilarity), color: ringProgressTrackColor }
                            ]} />
                        ) : <Text size="lg" fw={500} c="dimmed">N/A</Text>}
                      </Stack>
                    </Anchor>
                    <Anchor href="#competitor-analysis-section" underline="never">
                      <Stack align="center" gap={theme.spacing.xs} justify="flex-start" style={{ height: '100%', cursor: 'pointer' }}>
                        <Text c={isDark ? theme.colors.dark[0] : theme.colors.gray[8]} size="md" ta="center">Likely Prominence™</Text>
                        <Box style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                          <Badge size="xl" variant={isDark ? "outline" : "light"} color="grape" style={{ cursor: 'pointer' }}>{summaryStats.prominencePotential}</Badge>
                        </Box>
                      </Stack>
                    </Anchor>
                    <Anchor href="#competitor-analysis-section" underline="never">
                      <Stack align="center" gap={theme.spacing.xs} justify="flex-start" style={{ height: '100%', cursor: 'pointer' }}>
                        <Text c={isDark ? theme.colors.dark[0] : theme.colors.gray[8]} size="md" ta="center">Likely Stickiness™</Text>
                        <Box style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                          <Badge size="xl" variant={isDark ? "outline" : "light"} color="teal" style={{ cursor: 'pointer' }}>{summaryStats.stickinessExpected}</Badge>
                        </Box>
                      </Stack>
                    </Anchor>
                  </SimpleGrid>
                  <Box /* Disclaimer Bar */
                    mt="md" p="md" style={{ border: `1px solid ${isDark ? theme.colors.dark[4] : theme.colors.gray[4]}`, borderRadius: theme.radius.md, backgroundColor: isDark ? theme.colors.dark[6] : theme.colors.gray[0] }}
                  >
                    <Text c={isDark ? theme.colors.dark[1] : theme.colors.gray[7]} size="sm" ta="center">
                      <strong>Please Note:</strong> These summary metrics offer high-level indications; the Prominence and Stickiness badges, specifically, are derived from an analysis of top competitor articles. Click on each metric above to review the details and make an informed judgment. Refine your idea further to compare alternatives.
                    </Text>
                  </Box>
                  <Button
                    component="a"
                    href={briefBuilderLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    fullWidth
                    mt="lg"
                    size="lg"
                    color="green"
                    leftSection={<IconFileText size={20} />}
                    disabled={!briefBuilderLink} // The button will be disabled until the API responds
                  >
                    Generate Brief
                  </Button>
                </Paper>
              )}
              {apiResponse.published_overlaps !== undefined && (
                <Box id="published-history-section" pt="xl" mt="-xl">
                  <PublishedOverlaps data={apiResponse.published_overlaps} />
                </Box>
              )}
              {apiResponse.draft_conflict_details && (
                <Box id="draft-conflicts-section" pt="xl" mt="-xl">
                  <DraftConflict data={apiResponse.draft_conflict_details} />
                </Box>
              )}
              {apiResponse.competitor_analysis && (
                <Box id="competitor-analysis-section" pt="xl" mt="-xl">
                  <CompetitorAnalysis data={apiResponse.competitor_analysis} />
                </Box>
              )}
              {apiResponse.debug_timings && (
                <Box mt="lg">
                  <Title order={4} mb="xs" c="dimmed">AI Debug Timings:</Title>
                  <Box component="pre" fz="xs" c="dimmed" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {JSON.stringify(apiResponse.debug_timings, null, 2)}
                  </Box>
                </Box>
              )}
            </Paper>
          )}
        </Stack>
      </Container>
    </Box>
  );
}

export default FICSPage;